import React, { useState } from 'react';
import styled from 'styled-components';
import { api, buildVideoUrl } from '../../services/api';

interface Video {
  id: string;
  type: 'upload' | 'youtube';
  url: string;
  title?: string;
  thumbnail?: string;
  featured?: boolean;
  aspect_ratio?: '16:9' | '9:16';
}

interface VideoManagerProps {
  videos: Video[];
  onVideosChange: (videos: Video[]) => void;
  featuredVideoId?: string;
  onFeaturedChange?: (videoId: string | null) => void;
}

const Container = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const VideoList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const VideoItem = styled.div<{ $featured?: boolean }>`
  position: relative;
  background: ${({ theme }) => theme.colors.background};
  border: 2px solid ${({ theme, $featured }) => $featured ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const VideoThumbnail = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; // 16:9 aspect ratio
  background: #f3f4f6;
  overflow: hidden;

  img, iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const VideoInfo = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
`;

const VideoTitle = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.875rem;
  margin-bottom: ${({ theme }) => theme.spacing.sm};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const VideoActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme, $variant }) => 
    $variant === 'danger' ? theme.colors.error :
    $variant === 'primary' ? theme.colors.primary :
    theme.colors.background
  };
  color: ${({ theme, $variant }) => 
    $variant === 'secondary' ? theme.colors.text.primary : 'white'
  };
  border: 1px solid ${({ theme, $variant }) => 
    $variant === 'secondary' ? theme.colors.border : 'transparent'
  };
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm};
  right: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 1;
`;

const AddVideoSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.spacing.lg};
`;

const AddVideoOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const OptionButton = styled.button`
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}10;
  }

  h4 {
    font-size: 1rem;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  p {
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 1rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FileInput = styled.input`
  display: none;
`;

const VideoManager: React.FC<VideoManagerProps> = ({
  videos,
  onVideosChange,
  featuredVideoId,
  onFeaturedChange
}) => {
  const [addMode, setAddMode] = useState<'none' | 'upload' | 'youtube'>('none');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const extractYoutubeId = (url: string): string | null => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const getYoutubeThumbnail = (videoId: string): string => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const handleYoutubeAdd = () => {
    const videoId = extractYoutubeId(youtubeUrl);
    if (!videoId) {
      alert('URL do YouTube inválida');
      return;
    }

    const newVideo: Video = {
      id: `youtube_${Date.now()}`,
      type: 'youtube',
      url: `https://www.youtube.com/watch?v=${videoId}`,
      thumbnail: getYoutubeThumbnail(videoId),
      title: 'Vídeo do YouTube'
    };

    onVideosChange([...videos, newVideo]);
    setYoutubeUrl('');
    setAddMode('none');
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('video/')) {
      alert('Por favor, selecione um arquivo de vídeo válido');
      return;
    }

    // Validar tamanho (máximo 100MB)
    if (file.size > 100 * 1024 * 1024) {
      alert('O arquivo não pode ser maior que 100MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'video');
    formData.append('folder', 'videos');

    try {
      const response = await api.post('/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const newVideo: Video = {
        id: `upload_${Date.now()}`,
        type: 'upload',
        url: response.data.url,
        thumbnail: response.data.thumbnail || '',
        title: file.name.replace(/\.[^/.]+$/, '')
      };

      onVideosChange([...videos, newVideo]);
      setAddMode('none');
    } catch (error) {
      console.error('Erro ao fazer upload do vídeo:', error);
      alert('Erro ao fazer upload do vídeo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveVideo = (videoId: string) => {
    onVideosChange(videos.filter(v => v.id !== videoId));
    if (featuredVideoId === videoId && onFeaturedChange) {
      onFeaturedChange(null);
    }
  };

  const handleTitleChange = (videoId: string, title: string) => {
    onVideosChange(videos.map(v => 
      v.id === videoId ? { ...v, title } : v
    ));
  };

  const handleSetFeatured = (videoId: string) => {
    if (onFeaturedChange) {
      onFeaturedChange(featuredVideoId === videoId ? null : videoId);
    }
  };

  const renderVideoThumbnail = (video: Video) => {
    if (video.type === 'youtube') {
      const videoId = extractYoutubeId(video.url);
      return (
        <img 
          src={video.thumbnail || (videoId ? getYoutubeThumbnail(videoId) : '')} 
          alt={video.title} 
        />
      );
    } else {
      return (
        <video>
          <source src={buildVideoUrl(video.url)} type="video/mp4" />
        </video>
      );
    }
  };

  return (
    <Container>
      <Title>Vídeos da Propriedade</Title>

      {videos.length > 0 && (
        <VideoList>
          {videos.map((video) => (
            <VideoItem key={video.id} $featured={featuredVideoId === video.id}>
              {featuredVideoId === video.id && <FeaturedBadge>Em Destaque</FeaturedBadge>}
              <VideoThumbnail>
                {renderVideoThumbnail(video)}
              </VideoThumbnail>
              <VideoInfo>
                <VideoTitle
                  type="text"
                  value={video.title || ''}
                  onChange={(e) => handleTitleChange(video.id, e.target.value)}
                  placeholder="Título do vídeo"
                />
                <VideoActions>
                  <Button
                    $variant={featuredVideoId === video.id ? 'secondary' : 'primary'}
                    onClick={() => handleSetFeatured(video.id)}
                  >
                    {featuredVideoId === video.id ? '★ Destacado' : '☆ Destacar'}
                  </Button>
                  <Button $variant="danger" onClick={() => handleRemoveVideo(video.id)}>
                    Remover
                  </Button>
                </VideoActions>
              </VideoInfo>
            </VideoItem>
          ))}
        </VideoList>
      )}

      <AddVideoSection>
        {addMode === 'none' ? (
          <AddVideoOptions>
            <OptionButton onClick={() => setAddMode('upload')}>
              <h4>📤 Upload de Vídeo</h4>
              <p>Faça upload de um vídeo do seu computador</p>
            </OptionButton>
            <OptionButton onClick={() => setAddMode('youtube')}>
              <h4>🎥 Link do YouTube</h4>
              <p>Adicione um vídeo do YouTube</p>
            </OptionButton>
          </AddVideoOptions>
        ) : addMode === 'youtube' ? (
          <div>
            <Input
              type="text"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="Cole o link do YouTube aqui..."
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button $variant="primary" onClick={handleYoutubeAdd}>
                Adicionar Vídeo
              </Button>
              <Button $variant="secondary" onClick={() => setAddMode('none')}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <FileInput
              id="video-upload"
              type="file"
              accept="video/*"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <label htmlFor="video-upload">
              <Button as="span" $variant="primary" style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
                {uploading ? 'Enviando...' : 'Selecionar Vídeo'}
              </Button>
            </label>
            <Button $variant="secondary" onClick={() => setAddMode('none')} style={{ marginLeft: '8px' }}>
              Cancelar
            </Button>
          </div>
        )}
      </AddVideoSection>
    </Container>
  );
};

export default VideoManager;
